import { createElement } from "react";
import { Link, router } from "@inertiajs/react";
import {
  ArrowLeftIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { AdminLayout } from "../../../layouts/admin-layout";

interface Partner {
  id: number;
  name: string;
  slug: string;
  email: string;
  company_name: string | null;
  website_url: string | null;
  referral_plan: string | null;
  commission_rate: number;
  commission_duration_months: number;
  status: string;
  notes: string | null;
  signup_count: number;
  conversion_count: number;
  total_revenue_cents: number;
  total_commission_cents: number;
  total_commission_paid_cents: number;
  accepted_terms_at: string | null;
  referral_url: string | null;
  created_at: string;
  updated_at: string;
}

interface Referral {
  id: number;
  user_name: string | null;
  user_email: string | null;
  status: string;
  clicked_at: string | null;
  signed_up_at: string | null;
  first_paid_at: string | null;
  paid_at: string | null;
  revenue_cents: number;
  commission_cents: number;
  created_at: string;
}

interface PartnerShowProps {
  partner: Partner;
  referrals: Referral[];
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return "Never";
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCents(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) {
    const m = dollars / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (dollars >= 10_000) {
    const k = dollars / 1_000;
    return `$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return `$${dollars.toFixed(2)}`;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    active: "bg-green-100 text-green-800",
    inactive: "bg-red-100 text-red-800",
    clicked: "bg-blue-100 text-blue-800",
    signed_up: "bg-indigo-100 text-indigo-800",
    paid: "bg-green-100 text-green-800",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100 text-gray-500"}`}
    >
      {status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-3 border-b border-border last:border-0">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

function CopyableUrl({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-2">
      <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{url}</code>
      <button
        onClick={handleCopy}
        className="text-muted-foreground hover:text-foreground transition-colors"
        title="Copy to clipboard"
      >
        {copied ? (
          <CheckIcon className="w-4 h-4 text-green-500" />
        ) : (
          <ClipboardDocumentIcon className="w-4 h-4" />
        )}
      </button>
    </div>
  );
}

function PartnerShow({ partner, referrals }: PartnerShowProps) {
  const handleStatusChange = (newStatus: string) => {
    router.put(`/admin/partners/${partner.id}`, {
      partner: { status: newStatus },
    });
  };

  const referralUrl = partner.referral_url || `launch10.ai?ref=${partner.slug}`;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <header className="mb-8">
        <Link
          href="/admin/partners"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Partners
        </Link>

        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-foreground">
                {partner.name}
              </h1>
              <StatusBadge status={partner.status} />
            </div>
            <p className="text-muted-foreground mt-1">{partner.email}</p>
            {partner.company_name && (
              <p className="text-muted-foreground text-sm">
                {partner.company_name}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {partner.status === "active" && (
              <button
                onClick={() => handleStatusChange("inactive")}
                className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Deactivate
              </button>
            )}
            {partner.status === "inactive" && (
              <button
                onClick={() => handleStatusChange("active")}
                className="px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                Reactivate
              </button>
            )}
            <Link
              href={`/admin/partners/${partner.id}/edit`}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <PencilIcon className="w-4 h-4" />
              Edit
            </Link>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Partner Info */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Partner Details
          </h2>
          <InfoRow label="Slug" value={<code className="text-xs bg-muted px-2 py-1 rounded font-mono">{partner.slug}</code>} />
          <InfoRow label="Email" value={partner.email} />
          <InfoRow label="Company" value={partner.company_name || "—"} />
          <InfoRow
            label="Website"
            value={
              partner.website_url ? (
                <a
                  href={partner.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80"
                >
                  {partner.website_url}
                </a>
              ) : (
                "—"
              )
            }
          />
          <InfoRow label="Commission Rate" value={`${partner.commission_rate}%`} />
          <InfoRow label="Duration" value={`${partner.commission_duration_months} months`} />
          <InfoRow label="Terms Accepted" value={formatDateTime(partner.accepted_terms_at)} />
          <InfoRow label="Created" value={formatDateTime(partner.created_at)} />
        </div>

        {/* Metrics */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            Metrics
          </h2>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <div className="text-2xl font-bold text-foreground">
                {partner.signup_count}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Signups</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <div className="text-2xl font-bold text-foreground">
                {partner.conversion_count}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Conversions
              </div>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <div className="text-2xl font-bold text-foreground">
                {formatCents(partner.total_revenue_cents)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Revenue</div>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <div className="text-2xl font-bold text-foreground">
                {formatCents(partner.total_commission_cents)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Total Commission
              </div>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <div className="text-2xl font-bold text-foreground">
                {formatCents(partner.total_commission_paid_cents)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Commission Paid
              </div>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <div className={`text-2xl font-bold ${partner.total_commission_cents - partner.total_commission_paid_cents > 0 ? "text-amber-600" : "text-green-600"}`}>
                {formatCents(partner.total_commission_cents - partner.total_commission_paid_cents)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Commission Owed
              </div>
            </div>
          </div>

          <h3 className="text-sm font-semibold text-foreground mb-2">
            Referral URL
          </h3>
          <CopyableUrl url={referralUrl} />
        </div>
      </div>

      {/* Referral plan */}
      {partner.referral_plan && (
        <div className="bg-card rounded-lg border border-border p-6 mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-2">
            Referral Plan
          </h2>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {partner.referral_plan}
          </p>
        </div>
      )}

      {/* Notes */}
      {partner.notes && (
        <div className="bg-card rounded-lg border border-border p-6 mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-2">
            Admin Notes
          </h2>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {partner.notes}
          </p>
        </div>
      )}

      {/* Referrals table */}
      <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Referrals ({referrals.length})
          </h2>
          <a
            href={`/admin/partners/${partner.id}/export_referrals`}
            download={`${partner.slug}-referrals.csv`}
            className="text-sm text-primary hover:text-primary/80 transition-colors"
          >
            Export CSV
          </a>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                  User
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                  Signed Up
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">
                  First Paid
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">
                  Revenue
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">
                  Commission
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {referrals.map((referral) => (
                <tr
                  key={referral.id}
                  className="hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div>
                      <span className="font-medium text-foreground">
                        {referral.user_name || "—"}
                      </span>
                      <div className="text-xs text-muted-foreground">
                        {referral.user_email || "—"}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={referral.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDateTime(referral.signed_up_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDateTime(referral.first_paid_at)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-foreground">
                    {formatCents(referral.revenue_cents)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-foreground">
                    {formatCents(referral.commission_cents)}
                  </td>
                </tr>
              ))}
              {referrals.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    No referrals yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

PartnerShow.layout = (page: React.ReactNode) =>
  createElement(AdminLayout, null, page);

export default PartnerShow;
